import { combineReducers, createAction, createReducer, isAnyOf } from '@reduxjs/toolkit';
import { message } from 'common/services/message';
import { TFunction } from 'i18next';

import api from 'common/api';
import { TokenState } from 'common/api/token';
import { AppState } from 'common/models/AppState';
import { DocumentSignaturesFormValues } from 'common/models/Forms';
import { TokenDeliveryMethod, TokenType } from 'common/models/Token';
import { SignatureScope } from 'common/models/Transaction';
import { UserProfile } from 'common/models/User';
import { PdfRenderer } from 'common/pdf/pdf-renderer';
import i18n from 'common/services/i18n';
import { selectUserProfile } from 'features/auth/ducks';
import {
  loadTransactionActions,
  selectForm,
  selectFormTransaction,
  selectInformationsCustomerInfo1,
} from 'features/form/ducks';

import { startListening } from '../../../../root/store/listener-middleware';
import { assertFormValidForSubmission } from '../../validation/formSubmissionValidation';
import verificationLog, { initialVerificationLogState, VerificationLogState } from './features/verification-log/ducks';

export function selectClientSignature(state: AppState) {
  return selectForm(state).documents.signatures;
}

export function selectDocumentsToBeSigned(state: AppState) {
  return selectClientSignature(state).selectedDocuments;
}

export const resetSignaturesStateAction = createAction('@@Documents/Signatures/RESET');

export const requestSignatureCodeAction = createAction('@@Documents/Signatures/REQUEST_CODE');

export const submitSignatureCodeAction = createAction<DocumentSignaturesFormValues['code']>(
  '@@Documents/Signatures/SUBMIT_CODE',
);

export const setSelectedDocumentsAction = createAction<{ document: SignatureScope; isSelected: boolean }>(
  '@@Documents/Signatures/SET_SELECTED_DOCUMENTS',
);

export const forwardDocumentsAction = createAction<SignatureScope[]>('@@Documents/Signatures/FORWARD_SIGNED');

export const setDocumentUploadStatusAction = createAction<UploadState>('@@Documents/Signatures/SET_UPLOAD_STATUS');

const changeSignaturesStateAction = createAction<TokenState>('@@Documents/Signatures/CHANGE_STATE');

const updateSignaturesStateAction = createAction<Partial<SignaturesState>>('@@Documents/Signatures/UPDATE_STATE');

export interface SignaturesState {
  selectedDocuments: SignatureScope[];
  uploadStatus?: UploadState;
  status: TokenState;
  attemptsLeft?: number;
  httpStatus?: number;
}

export const initialSignaturesFormState: SignaturesState = {
  selectedDocuments: [],
  status: TokenState.DEFAULT,
};

export enum UploadState {
  FAILED = 'failed',
  SUCCESS = 'success',
  UPLOADING = 'uploading',
}

const signatures = createReducer(initialSignaturesFormState, (builder) => {
  builder
    .addCase(setSelectedDocumentsAction, (state, { payload: { isSelected, document } }) => {
      if (isSelected) {
        state.selectedDocuments.push(document);
      } else {
        state.selectedDocuments = state.selectedDocuments.filter((selectedDocument) => selectedDocument !== document);
      }
    })
    .addCase(updateSignaturesStateAction, (state, { payload }) => {
      Object.assign(state, payload);
    })
    .addCase(changeSignaturesStateAction, (state, { payload }) => ({
      selectedDocuments: state.selectedDocuments,
      status: payload,
    }))
    .addCase(requestSignatureCodeAction, (state) => {
      state.status = TokenState.REQUESTED;
    })
    .addCase(submitSignatureCodeAction, (state) => {
      state.status = TokenState.SUBMITTED;
    })
    .addCase(setDocumentUploadStatusAction, (state, { payload }) => {
      state.uploadStatus = payload;
    })
    .addCase(resetSignaturesStateAction, () => initialSignaturesFormState);
});

export interface DocumentsState {
  signatures: SignaturesState;
  verificationLog: VerificationLogState;
}

export const initialDocumentsState = {
  signatures: initialSignaturesFormState,
  verificationLog: initialVerificationLogState,
};

export default combineReducers({
  signatures,
  verificationLog,
});

// Listeners
startListening({
  actionCreator: requestSignatureCodeAction,
  effect: async (_action, listenerApi) => {
    listenerApi.cancelActiveListeners();
    // Prevent accidental double-click submissions
    await listenerApi.delay(250);

    const state = listenerApi.getState();
    const transaction = selectFormTransaction(state)!;

    if (!assertFormValidForSubmission(state)) {
      listenerApi.dispatch(
        updateSignaturesStateAction({
          status: TokenState.DEFAULT,
        }),
      );
      return;
    }

    const signatureScopes = selectDocumentsToBeSigned(state);

    try {
      const { ok, status } = await api.token.create(
        transaction._id,
        TokenType.SIGNATURE,
        TokenDeliveryMethod.PHONE,
        signatureScopes,
      );

      listenerApi.dispatch(
        updateSignaturesStateAction({
          status: ok ? TokenState.DELIVERED : TokenState.DELIVERY_ERROR,
          httpStatus: status,
        }),
      );
    } catch {
      listenerApi.dispatch(
        updateSignaturesStateAction({
          status: TokenState.DELIVERY_ERROR,
        }),
      );
    }
  },
});

startListening({
  actionCreator: submitSignatureCodeAction,
  effect: async (action, listenerApi) => {
    listenerApi.cancelActiveListeners();
    const state = listenerApi.getState();

    if (!assertFormValidForSubmission(state)) {
      listenerApi.dispatch(
        updateSignaturesStateAction({
          status: TokenState.DEFAULT,
        }),
      );
      return;
    }

    const transaction = selectFormTransaction(state)!;
    const { phone } = selectInformationsCustomerInfo1(state)!;
    const selectedDocuments = selectDocumentsToBeSigned(state);

    const { ok, status, response } = await api.token.verify(
      transaction._id,
      TokenType.SIGNATURE,
      TokenDeliveryMethod.PHONE,
      `${phone.prefix}${phone.number}`,
      action.payload,
    );

    listenerApi.dispatch(
      updateSignaturesStateAction({
        status: ok ? TokenState.VERIFIED : TokenState.SUBMISSION_ERROR,
        httpStatus: status,
        attemptsLeft: response?.data?.attemptsLeft,
        ...(ok && { selectedDocuments: [] }),
      }),
    );

    // Load the transaction again to reflect the updated signatures state set by BE
    listenerApi.dispatch(loadTransactionActions.request(transaction._id));
    await listenerApi.take(isAnyOf(loadTransactionActions.success, loadTransactionActions.failure));

    if (ok) {
      listenerApi.dispatch(setDocumentUploadStatusAction(UploadState.UPLOADING));
      listenerApi.dispatch(forwardDocumentsAction(selectedDocuments));
    }
  },
});

startListening({
  actionCreator: forwardDocumentsAction,
  effect: async (action, listenerApi) => {
    const t: TFunction = await i18n;

    try {
      const state = listenerApi.getState();
      if (!assertFormValidForSubmission(state)) {
        listenerApi.dispatch(setDocumentUploadStatusAction(UploadState.FAILED));
        return;
      }
      const renderer = new PdfRenderer(state);
      const documents = await renderer.createBlobs(action.payload);
      const transaction = selectFormTransaction(listenerApi.getState())!;
      const { ok } = await api.transaction.forwardDocuments(transaction._id, documents);

      ok
        ? message.success(t('forms:signatureForm.uploadSuccess'))
        : message.error(t('forms:signatureForm.uploadFailure'));

      listenerApi.dispatch(setDocumentUploadStatusAction(ok ? UploadState.SUCCESS : UploadState.FAILED));
    } catch (error) {
      const user: UserProfile = selectUserProfile(listenerApi.getState())!;
      api.journal.postError(error as Error, { userId: user.id });
      listenerApi.dispatch(setDocumentUploadStatusAction(UploadState.FAILED));
      message.error(t('forms:signatureForm.uploadFailure'));
    }
  },
});
